import React from "react";
import { Button } from "../ui/button";
import {
  ChevronDown, ChevronRight, Home, Trash2, Plus,
  Calculator, RectangleHorizontal, Square, DoorOpen,
} from "lucide-react";
import { fmtEUR } from "../../lib/calculator";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";
import {
  WALL_PRESET, CEILING_PRESET, FLOOR_PRESET, DOOR_PRESET, WINDOW_PRESET, EXTRA_PRESETS,
} from "../../lib/measurePresets";
import MeasurementRow from "./MeasurementRow";
import ExtraRow from "./ExtraRow";

export default function RoomCard({
  room, measurements, extras, summary, collapsed, onToggle, onRename, onRemove,
  priceCodes, onAddMeasurement, onAddExtra, onQuickCalc,
  onUpdateM, onRemoveM, onDuplicateM,
  onAddDeduction, onUpdDeduction, onRemDeduction,
  onUpdExtra, onRemExtra, onSwitchSimple,
}) {
  const wallsCount = measurements.filter((m) => m.formula === "wall").length;
  const ceilingsCount = measurements.filter((m) => m.formula === "ceiling").length;

  return (
    <div className="emma-card !p-0 overflow-hidden" data-testid={`room-card-${room.id}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#FBE9DF] to-transparent border-b border-[#E5E2DC]">
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 text-left min-w-0" data-testid={`room-toggle-${room.id}`}>
          {collapsed ? <ChevronRight className="w-5 h-5 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 flex-shrink-0" />}
          <Home className="w-5 h-5 text-[#D96C4A] flex-shrink-0" />
          <input
            value={room.name}
            onChange={(e) => onRename(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={autoSelectOnFocus}
            className="bg-transparent font-heading font-bold text-lg text-[#2C2C2A] border-none outline-none focus:bg-white px-2 py-1 rounded-lg min-w-0 flex-1"
            data-testid={`room-name-${room.id}`}
          />
        </button>
        <div className="flex items-center gap-3 flex-shrink-0">
          {summary && (
            <div className="text-right">
              <div className="text-xs text-[#4F4F4D] hidden sm:block">{wallsCount} wand · {ceilingsCount} plafond · {measurements.length + extras.length} regel(s)</div>
              <div className="font-heading font-bold text-[#D96C4A]" data-testid={`room-subtotal-${room.id}`}>
                {fmtEUR(summary.subtotal_excl)}
              </div>
            </div>
          )}
          <button onClick={onRemove} className="w-10 h-10 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F]" data-testid={`remove-room-${room.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-4">
          {/* v6 F12 — Legacy stair-room banner: rooms met room_type="stair"
              blijven werken, maar we sturen de gebruiker naar "Werkzone toevoegen → Trap"
              voor betere trap-specifieke UX. */}
          {room.room_type === "stair" && (
            <div className="rounded-xl border-2 border-[#7F4124]/40 bg-[#F7EDE4] px-4 py-3 flex items-start gap-2" data-testid={`stair-legacy-banner-${room.id}`}>
              <span className="text-[#7F4124] font-bold text-lg leading-none">⚠</span>
              <div className="text-[12px] text-[#7F4124] flex-1">
                <strong>Trap staat nog als "ruimte".</strong> Voor de nieuwe trap-flow met sjabloon-picker en trap-specifieke werken:
                verwijder deze ruimte en voeg een <strong>Werkzone → Trap</strong> toe. Deze ruimte blijft werken tot je hem verwijdert.
              </div>
            </div>
          )}
          {onSwitchSimple && (
            <button onClick={onSwitchSimple} className="text-[11px] text-[#D96C4A] hover:underline self-start" data-testid={`switch-simple-${room.id}`}>
              ← Eenvoudige modus
            </button>
          )}
          {/* Add buttons */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => onQuickCalc()} className="h-10 rounded-xl text-xs gap-1 bg-[#FBE9DF] border-[#D96C4A] text-[#D96C4A] hover:bg-[#D96C4A] hover:text-white" data-testid={`quick-calc-${room.id}`}>
              <Calculator className="w-3.5 h-3.5" /> Snelle ruimteberekening
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement(WALL_PRESET)} className="h-10 rounded-xl text-xs gap-1 hover:bg-[#D96C4A] hover:text-white" data-testid={`add-wall-${room.id}`}>
              <RectangleHorizontal className="w-3.5 h-3.5" /> Wand
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement(CEILING_PRESET)} className="h-10 rounded-xl text-xs gap-1 hover:bg-[#8A9A86] hover:text-white" data-testid={`add-ceiling-${room.id}`}>
              <Square className="w-3.5 h-3.5" /> Plafond
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement(FLOOR_PRESET)} className="h-10 rounded-xl text-xs gap-1" data-testid={`add-floor-${room.id}`}>
              <Square className="w-3.5 h-3.5" /> Vloer
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement(DOOR_PRESET)} className="h-10 rounded-xl text-xs gap-1" data-testid={`add-door-${room.id}`}>
              <DoorOpen className="w-3.5 h-3.5" /> Deur
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement(WINDOW_PRESET)} className="h-10 rounded-xl text-xs gap-1" data-testid={`add-window-${room.id}`}>
              <DoorOpen className="w-3.5 h-3.5 rotate-90" /> Raam
            </Button>
            <Button variant="outline" onClick={() => onAddMeasurement()} className="h-10 rounded-xl text-xs gap-1 border-dashed" data-testid={`add-custom-${room.id}`}>
              <Plus className="w-3.5 h-3.5" /> Andere
            </Button>
          </div>

          {measurements.length === 0 && (
            <div className="text-sm text-[#4F4F4D] text-center py-6 border border-dashed border-[#E5E2DC] rounded-xl">
              Nog geen metingen in deze kamer.
            </div>
          )}

          {measurements.map((m, idx) => (
            <MeasurementRow key={m.id} m={m} idx={idx + 1} priceCodes={priceCodes}
              onChange={(p) => onUpdateM(m.id, p)}
              onRemove={() => onRemoveM(m.id)}
              onDuplicate={() => onDuplicateM(m.id)}
              onAddDeduction={() => onAddDeduction(m.id)}
              onUpdDeduction={(did, patch) => onUpdDeduction(m.id, did, patch)}
              onRemDeduction={(did) => onRemDeduction(m.id, did)}
            />
          ))}

          {/* Room extras */}
          {extras.length > 0 && (
            <div className="border-t border-[#E5E2DC] pt-4">
              <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#5D7259] mb-2">Extra's in deze kamer</div>
              {extras.map((e) => (
                <ExtraRow key={e.id} e={e} onChange={(p) => onUpdExtra(e.id, p)} onRemove={() => onRemExtra(e.id)} />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E5E2DC]">
            {EXTRA_PRESETS.map((p) => (
              <Button key={p.code} variant="outline" onClick={() => onAddExtra(p)} className="h-9 rounded-lg text-xs gap-1" data-testid={`room-extra-${room.id}-${p.code}`}>
                <Plus className="w-3 h-3" /> {p.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
